import { getSessionEstimatedArrivalStatus } from "./product.utils.js";

class VariantSizes extends HTMLElement {
  constructor() {
    super();
    this.canUpdateProductionTimelineLabels = true;
    this.allSizesAreMadeToOrder = false;
    this.allSizesArePreorder = false;
    this.allSizesAreMadeToOrderAndPreorder = false;
    this.allSizesAreWaitlist = false;
    this.numOfMadeToOrderSizes = 0;
    this.numOfPreorderSizes = 0;
    this.numOfWaitlistSizes = 0;

    /**
     * These structures are used to cross compare relevant variants when the Color is not enough to determine it.
     * Example: Products with Slit / No Slit, products with Not Personalized / Personalized
     * */
    this.matchingVariants = [];
    this.variantTitlesChecked = new Set();

    const defaultArrival = window.dualSaleStandardOptionName;
    const arrival = getSessionEstimatedArrivalStatus() || defaultArrival;

    // TODO: Ideally we lift the state up here and use as shared state
    this.state = {
      color: undefined,
      slit: undefined,
      personalization: undefined,
      arrival,
      size:
        window.sessionStorage.getItem("pdp-standard-size-preselect") ||
        undefined,
    };

    // String that combines the state properties
    this.variantString = undefined;
    this.dualSale = String(this.dataset.dualSale) === "true";
  }

  connectedCallback() {
    this.initializeElements();
    this.initializeListeners();
    this.render();
  }

  handleStateChanges() {
    this.resetSizeCounts();
    this.updateState();
    this.updateVariantString();
    this.updateMatchingVariants();
  }

  auditRushFee() {
    if (!this.dualSale) return;
    // If the Rush Fee is $0, MTO should be crossed-out and RTS should be selected. For MTO only products, we should automatically check MTO.
    const estimatedArrival = document.querySelector(
      "#estimated-arrival-legend",
    );
    const standardRadio = document.querySelector(
      '.standard-radios input[value="STANDARD PRODUCTION"]',
    );
    const rushRadio = document.querySelector(
      '.standard-radios input[value="RUSH PRODUCTION"]',
    );

    const rushFee = estimatedArrival?.dataset.rushFee;
    const isVariantMTOonly = rushRadio?.classList?.contains("waitlisted");
    if (rushFee === "$0" || isVariantMTOonly) {
      standardRadio.classList.toggle("waitlisted", !isVariantMTOonly);
      standardRadio.classList.toggle("disable-mto", !isVariantMTOonly);
      standardRadio.disabled = !isVariantMTOonly;
      (isVariantMTOonly ? standardRadio : rushRadio).click();
    }
  }

  updateUI() {
    if (this.dualSale) {
      this.updateProductionTimelineLabelsDualSale();
    } else {
      this.updateProductionTimelineLabels();
      this.determineIfAllSizesAreMadeToOrderOrPreorder();
      this.updateMessagingOnPDP();
    }
  }

  render() {
    this.handleStateChanges();
    this.auditRushFee();
    this.updateUI();
  }

  initializeElements() {
    this.product = document.querySelector(".product-wrapper");
    this.productionTimelineMessage =
      this.product.querySelector("#shipping-message");
    this.madeToOrderNote = this.product.querySelector("#madetoorder-text");
    this.preOrderNote = this.product.querySelector("#preorder-text");
    this.generalOrderNote = this.product.querySelector("#general-order-text");
    this.generalPreorderNote = this.product.querySelector(
      "#general-preorder-text",
    );
    this.bisMessage = this.product.querySelector("#bis-message");
    this.sizeDropdownItems = this.product.querySelectorAll(
      ".size-dropdown .dropdown-item",
    );
    this.slitVariationRadiosExist = this.product.querySelectorAll(
      ".slit-variation-radio",
    ).length;
    this.personalizationRadiosExist = this.product.querySelectorAll(
      ".personalization-input",
    ).length;
    this.arrivalRadiosExist = this.product.querySelectorAll(
      ".estimated-arrival-input",
    ).length;
    this.productRadios = this.product.querySelectorAll(
      ".product-radio:not(.product-fabric-radio)",
    );
  }

  initializeListeners = () => {
    if (this.productRadios) {
      this.productRadios.forEach((radio) => {
        radio.addEventListener("click", () => {
          this.render();
        });
      });
    }
  };

  resetSizeCounts = () => {
    this.numOfMadeToOrderSizes = 0;
    this.numOfPreorderSizes = 0;
  };

  updateState() {
    const variantOptionConfig = {
      color: {
        selector: ".standard-color-radios input",
        defaultToFirstAvailable: false,
      },
      slit: {
        selector: ".slit-variation-radio",
        defaultToFirstAvailable: false,
      },
      personalization: {
        selector: ".personalization-input",
        defaultToFirstAvailable: true,
      },
      arrival: {
        selector: ".estimated-arrival-input",
        defaultToFirstAvailable: false,
      },
    };

    // Set properties for this.state
    Object.entries(variantOptionConfig).forEach(
      ([variantOptionKey, config]) => {
        this.state[variantOptionKey] = this.getVariantOptionValue(
          config.selector,
          config.defaultToFirstAvailable,
        );
      },
    );
  }

  /**
   * Retrieves the value of a selected variant option from the DOM.
   * @param {string} selector - The CSS selector for the input elements.
   * @param {boolean} defaultToFirstAvailable - If true, defaults to the first available option if none are selected.
   * @returns {string|undefined} The value of the selected or default option.
   */
  getVariantOptionValue(selector, defaultToFirstAvailable = false) {
    const elements = this.product.querySelectorAll(selector);
    const checked = Array.from(elements).find((el) => el.checked);

    if (checked) {
      return checked.value;
    }
    if (defaultToFirstAvailable && elements.length > 0) {
      return elements[0].value;
    }
    return undefined;
  }

  updateVariantString = () => {
    const { color, slit, personalization, arrival } = this.state;

    if (this.slitVariationRadiosExist && slit) {
      this.canUpdateProductionTimelineLabels = true;
      this.variantString = `${color} / ${slit}`;
    } else if (this.personalizationRadiosExist && personalization) {
      this.canUpdateProductionTimelineLabels = true;
      this.variantString = `${color} / ${personalization}`;
    } else if (this.arrivalRadiosExist && arrival) {
      this.canUpdateProductionTimelineLabels = true;
      this.variantString = `${color} / ${arrival}`;
    } else if (
      this.slitVariationRadiosExist ||
      this.personalizationRadiosExist
    ) {
      this.canUpdateProductionTimelineLabels = false;
    } else if (color !== undefined) {
      this.canUpdateProductionTimelineLabels = true;
      this.variantString = color;
    }
  };

  updateMatchingVariants = () => {
    this.matchingVariants = [];
    this.variantTitlesChecked = new Set();
    const { color } = this.state;
    const variantOptionElements = this.product.querySelectorAll(
      ".variant-select option",
    );
    const variantOptions = [...variantOptionElements];

    variantOptions.forEach((option) => {
      const title = String(option.dataset.title);

      if (color) {
        const titleMatchesVariantString = title === this.variantString;
        let titleIncludesVariantStringAndColor =
          title.includes(this.variantString) && title.includes(`/ ${color}`);

        // Adjust the check for dual sale conditions
        if (this.dualSale) {
          titleIncludesVariantStringAndColor =
            title.includes(`/ ${color}`) &&
            option.dataset.options?.includes(window.dualSaleOptionName);
        }

        // Add to matchingVariants if either the title matches exactly or the title includes the variantString and the color
        if (titleMatchesVariantString || titleIncludesVariantStringAndColor) {
          this.matchingVariants.push(option);
        }
      } else if (
        !this.dualSale &&
        title &&
        !this.variantTitlesChecked.has(title)
      ) {
        // For non-dual sale scenarios without a color
        this.variantTitlesChecked.add(title);
        this.matchingVariants.push(option);
      }
    });
  };

  /**
   * Handles the production timeline labels that appear in the dropdown.
   */
  updateProductionTimelineLabels = () => {
    if (!this.canUpdateProductionTimelineLabels) return;

    if (this.sizeDropdownItems.length === 0) {
      if (typeof this.matchingVariants[0] !== "undefined") {
        if (this.matchingVariants[0]) {
          const { dataset } = this.matchingVariants[0];
          if (dataset.madetoorder) {
            this.numOfMadeToOrderSizes += 1;
          } else if (dataset.preorder) {
            this.numOfPreorderSizes += 1;
          } else if (parseInt(dataset.quantity, 10) <= 0) {
            this.numOfWaitlistSizes += 1;
          }
        }
      }
    } else {
      for (let i = 0; i < this.sizeDropdownItems.length; i += 1) {
        const currentSizeItem = this.sizeDropdownItems[i];
        const productionTimelineLabel = currentSizeItem.querySelector(
          ".production-timeline-label",
        );
        const matchedVariant = this.matchingVariants.find(
          (variant) => variant.dataset.size === currentSizeItem.dataset.value,
        );

        if (matchedVariant) {
          const { dataset } = matchedVariant;
          const currentProductionTimeline =
            VariantSizes.getProductionTimeline(dataset);

          if (dataset.madetoorder) {
            this.numOfMadeToOrderSizes += 1;
          } else if (dataset.preorder) {
            this.numOfPreorderSizes += 1;
          } else if (parseInt(dataset.quantity, 10) <= 0) {
            this.numOfWaitlistSizes += 1;
          }

          currentSizeItem.style.backgroundColor =
            VariantSizes.labelStyles[
              currentProductionTimeline
            ].parentStyleBackground;
          currentSizeItem.querySelector(".dropdown-item__size").style.opacity =
            VariantSizes.labelStyles[currentProductionTimeline].sizeOpacity;
          VariantSizes.updateLabelStyles(
            currentSizeItem,
            productionTimelineLabel,
            currentProductionTimeline,
          );
        }
      }
    }
  };

  /**
   * Handles the production timeline labels that appear in the dropdown when on a dual sale product.
   */
  updateProductionTimelineLabelsDualSale = () => {
    if (!this.canUpdateProductionTimelineLabels) return;

    // Split matchingVariants into 2 arrays one for Made To Order and Rush
    const madeToOrderVariants = [];
    const rushVariants = [];

    // Populate arrays by checking for keywords "standard" and "rush"
    this.matchingVariants.forEach((variant) => {
      const variantTitle = String(variant.dataset.title).toLowerCase();
      const standardTitle = window.dualSaleStandardOptionName.toLowerCase();
      const rushTitle = window.dualSaleRushOptionName.toLowerCase();
      if (variantTitle.includes(standardTitle)) {
        madeToOrderVariants.push(variant);
      } else if (variantTitle.includes(rushTitle)) {
        rushVariants.push(variant);
      }
    });

    for (let i = 0; i < this.sizeDropdownItems.length; i += 1) {
      const currentSizeItem = this.sizeDropdownItems[i];
      const productionTimelineLabel = currentSizeItem.querySelector(
        ".production-timeline-label",
      );
      /**
       * Get the inventory policy of the standard variant.
       *
       * Then check both the inventory policy of standard and the availability of the rush variant.
       */
      let standardInventoryPolicy;
      const matchingStandardVariant = madeToOrderVariants.find(
        (variant) => variant.dataset.size === currentSizeItem.dataset.value,
      );
      if (matchingStandardVariant) {
        const { dataset } = matchingStandardVariant;
        standardInventoryPolicy = VariantSizes.getInventoryPolicy(dataset);
      }

      const matchingRushVariant = rushVariants.find(
        (variant) => variant.dataset.size === currentSizeItem.dataset.value,
      );

      if (matchingRushVariant) {
        const { dataset } = matchingRushVariant;
        let currentProductionTimeline =
          VariantSizes.getProductionTimeline(dataset);

        // Reset if both are available, since we won't need to show any label
        if (dataset.quantity > 0) {
          currentProductionTimeline = "reset";
        } else {
          /**
           * Show made to order otherwise
           * This may need to change depending on how logic shifts for dual sale
           * */
          currentProductionTimeline = "madetoorderonly";
        }

        /**
         * Update currentProductionTimeline when inventory policy (continue selling when out of stock) is "deny". When policy is "deny", we're enabling waitlisted/discontinued products.
         */
        if (standardInventoryPolicy === "deny") {
          if (currentProductionTimeline === "madetoorderonly") {
            // If MTO is available only and it's sold out, mark OOS; else item can be waitlisted.
            if (dataset.quantity <= 0) {
              currentProductionTimeline = "outofstock";
            } else {
              currentProductionTimeline = "waitlist";
            }
          } else if (dataset.quantity > 0) {
            // If RTS qty is available, mark as RTS
            currentProductionTimeline = "readytoship";
          } else {
            // Reset since this specific size's rush variant is available and standard can be waitlisted.
            currentProductionTimeline = "reset";
          }
        }

        currentSizeItem.style.backgroundColor =
          VariantSizes.labelStyles[
            currentProductionTimeline
          ].parentStyleBackground;
        currentSizeItem.querySelector(".dropdown-item__size").style.opacity =
          VariantSizes.labelStyles[currentProductionTimeline].sizeOpacity;
        VariantSizes.updateLabelStyles(
          currentSizeItem,
          productionTimelineLabel,
          currentProductionTimeline,
        );

        // Patch for waitlisted Dual Sale products to show the BIS message instead of Production Timeline.
        if (currentProductionTimeline === "waitlist") {
          VariantSizes.showElements(this.bisMessage);
          VariantSizes.hideElements(this.productionTimelineMessage);
        }
      }
      // If product variant is discontinued, sell out the RTS and cross out MTO.
      if (
        matchingStandardVariant.dataset.size === window?.selectedSize?.value &&
        matchingStandardVariant.dataset.variantInventoryPolicy === "deny" &&
        matchingStandardVariant.dataset.quantity === "0"
      ) {
        const standardRadio = document.querySelector(
          '.standard-radios input[value="STANDARD PRODUCTION"]',
        );
        standardRadio.disabled = true;
        standardRadio.classList.add("waitlisted");
      }
    }
  };

  static updateLabelStyles(
    currentSizeItem,
    productionTimelineLabel,
    currentProductionTimeline,
  ) {
    // Clone the label element to avoid direct modifications
    const labelClone = productionTimelineLabel.cloneNode(true);

    const styles = VariantSizes.labelStyles[currentProductionTimeline];

    // Set properties of the cloned label
    labelClone.style.opacity = styles.labelOpacity;
    labelClone.innerText = styles.text;
    labelClone.style.backgroundColor = styles.styleBackground;
    labelClone.style.padding = styles.padding;

    // Replace the original label with the modified clone
    currentSizeItem.replaceChild(labelClone, productionTimelineLabel);
  }

  static labelStyles = {
    madetoorder: {
      text: "MADE-TO-ORDER",
      styleBackground: "#E4EDEE",
      sizeOpacity: "1",
      labelOpacity: "1",
    },
    madetoorderonly: {
      text: "MADE-TO-ORDER ONLY",
      styleBackground: "#E4EDEE",
      sizeOpacity: "1",
      labelOpacity: "1",
    },
    preorder: {
      text: "PREORDER",
      styleBackground: "#E4EDEE",
      sizeOpacity: "1",
      labelOpacity: "1",
    },
    readytoship: {
      text: "SHIPS NOW",
      styleBackground: "#E9CFE0",
      sizeOpacity: "1",
      labelOpacity: "1",
    },
    waitlist: {
      text: "WAITLIST",
      styleBackground: "transparent",
      sizeOpacity: "1",
      labelOpacity: "1",
      padding: "0 0.25rem",
    },
    outofstock: {
      text: "OUT OF STOCK",
      styleBackground: "transparent",
      parentStyleBackground: "#F3F1EE",
      sizeOpacity: "1",
      labelOpacity: "1",
      padding: "0 0.25rem",
    },
    reset: {
      text: "",
      styleBackground: "transparent",
      sizeOpacity: "1",
      labelOpacity: "1",
    },
  };

  static getProductionTimeline = (dataset) => {
    if (dataset.madetoorder) return "madetoorder";
    if (dataset.preorder) return "preorder";
    if (dataset.readytoship) return "readytoship";
    if (dataset.nowaitlist === "true" && parseInt(dataset.quantity, 10) <= 0)
      return "outofstock";
    if (parseInt(dataset.quantity, 10) <= 0) return "waitlist";
    return "reset";
  };

  /**
   * Returns the inventory policy given the variant's dataset.
   *
   * A policy of "continue" means "Continue selling when out of stock" is enabled.
   *
   * @param dataset - Dataset of the variant created from product--shipping-dates.liquid
   * @return {string} - "continue" or "deny"
   */
  static getInventoryPolicy = (dataset) => dataset.variantInventoryPolicy;

  determineIfAllSizesAreMadeToOrderOrPreorder = () => {
    // Check if all sizes are Made To Order or Preorder
    if (this.numOfMadeToOrderSizes > 0 && this.numOfPreorderSizes > 0) {
      this.allSizesAreMadeToOrderAndPreorder =
        this.matchingVariants.length ===
        this.numOfMadeToOrderSizes + this.numOfPreorderSizes;
      window.globalAllSizesAreMtoAndPreorder =
        this.allSizesAreMadeToOrderAndPreorder;
    }
    // Check if all sizes are Made To Order
    else if (
      this.numOfMadeToOrderSizes > 0 &&
      this.matchingVariants.length > 0
    ) {
      this.allSizesAreMadeToOrder =
        this.matchingVariants.length === this.numOfMadeToOrderSizes;
      window.globalAllSizesAreMto = this.allSizesAreMadeToOrder;
    }
    // Check if all sizes are Preorder
    else if (this.numOfPreorderSizes > 0 && this.matchingVariants.length > 0) {
      this.allSizesArePreorder =
        this.matchingVariants.length === this.numOfPreorderSizes;
      window.globalAllSizesArePreorder = this.allSizesArePreorder;
    }
    // check if all sizes are Waitlist
    else if (this.numOfWaitlistSizes > 0 && this.matchingVariants.length > 0) {
      this.allSizesAreWaitlist =
        this.matchingVariants.length === this.numOfWaitlistSizes;
      window.globalAllSizesAreWaitlist = this.allSizesAreWaitlist;
    }
  };

  // TODO: Break out into own component that represents PDP messaging
  updateMessagingOnPDP = () => {
    const allOptionsAreSelected = this.allOptionsSelected();
    if (allOptionsAreSelected) {
      this.togglePDPmessagingWhenAllOptionsAreSelected();
    } else {
      this.togglePDPmessagingWhenSomeOptionsAreSelected(
        this.allSizesAreMadeToOrder,
        this.allSizesArePreorder,
        this.allSizesAreMadeToOrderAndPreorder,
        this.allSizesAreWaitlist,
        this.numOfMadeToOrderSizes,
        this.numOfPreorderSizes,
      );
      this.changeCtaForMadeToOrderAndPreorder();
      this.swapHexOfCta();
    }
  };

  togglePDPmessagingWhenSomeOptionsAreSelected = (
    allSizesAreMto,
    allSizesArePreorder,
    allSizesAreMtoAndPreorder,
    allSizesAreWaitlist,
    numOfMtoSizes,
    numOfPreorderSizes,
  ) => {
    // Hide all messaging by default
    VariantSizes.hideElements(
      this.madeToOrderNote,
      this.preOrderNote,
      this.generalOrderNote,
      this.generalPreorderNote,
      this.productionTimelineMessage,
      this.bisMessage,
    );

    // Determine which messages to show
    if (allSizesAreMto === true) {
      VariantSizes.showElements(
        this.madeToOrderNote,
        this.productionTimelineMessage,
      );
    } else if (allSizesArePreorder === true) {
      VariantSizes.showElements(this.preOrderNote);
    } else if (allSizesAreMtoAndPreorder) {
      VariantSizes.showElements(this.madeToOrderNote);
    } else if (allSizesAreWaitlist) {
      VariantSizes.showElements(this.bisMessage);
    } else if (numOfMtoSizes > 0) {
      VariantSizes.showElements(this.generalOrderNote);
    } else if (numOfPreorderSizes > 0) {
      VariantSizes.showElements(this.generalPreorderNote);
    }
  };

  togglePDPmessagingWhenAllOptionsAreSelected = () => {
    const select = this.product.querySelector(".variant-select");
    const option = select?.options[select.selectedIndex];
    const preorderArriveNote = this.preOrderNote?.querySelector(
      ".preorder-arrive-note",
    );
    if (option) {
      // Hide all messaging by default
      VariantSizes.hideElements(
        this.madeToOrderNote,
        this.preOrderNote,
        this.generalOrderNote,
        this.generalPreorderNote,
        this.productionTimelineMessage,
        this.bisMessage,
      );

      // Determine which messages to show
      if (option.dataset.madetoorder) {
        VariantSizes.showElements(
          this.madeToOrderNote,
          this.productionTimelineMessage,
        );
      } else if (option.dataset.preorder) {
        if (preorderArriveNote) {
          preorderArriveNote.innerText = `${option.dataset.preorder}`;
        }
        VariantSizes.showElements(this.preOrderNote);
      } else if (option.dataset.backinstock) {
        VariantSizes.showElements(this.bisMessage);
      }
    }
  };

  /**
   * Below are duplicated resources from product.js
   */

  // TODO: Remove once product.js can use modules
  changeCtaForMadeToOrderAndPreorder = () => {
    const allOptionsAreSelected = this.allOptionsSelected();
    if (!allOptionsAreSelected) {
      if (window.globalAllSizesArePreorder) {
        const addToCartButton = this.product.querySelector("#pdp-atc");
        addToCartButton.classList.remove(
          addToCartButton.classList.value
            .split(" ")
            .find((cssClass) => cssClass.indexOf("bg-") !== -1),
        );
        this.product.querySelector("#pdp-atc").textContent = "Preorder";
      }
    }
  };

  // TODO: Remove once product.js can use modules
  swapHexOfCta = () => {
    if (this.product.querySelector("#pdp-atc")) {
      const addToCartButton = this.product.querySelector("#pdp-atc");
      const addToCartButtonText = addToCartButton?.textContent
        .trim()
        .toLowerCase();

      addToCartButton.classList.remove(
        "button-primary",
        "button-primary-alt",
        "button-secondary",
        "button-special",
      );

      // Update color based on arrival state
      if (addToCartButtonText === "preorder") {
        addToCartButton.classList.add("button-special");
      } else {
        addToCartButton.classList.add("button-primary");
      }
    }
  };

  // TODO: Remove once product.js can use modules
  static showElements = (...elements) => {
    elements.forEach((el) => {
      el?.classList.remove("none", "hidden");
    });
  };

  // TODO: Remove once product.js can use modules
  static hideElements = (...elements) => {
    elements.forEach((el) => {
      el?.classList.add("none", "hidden");
    });
  };

  // TODO: Remove once product.js can use modules
  allOptionsSelected = () => {
    const slitVariationRadios = this.product.querySelectorAll(
      ".slit-variation-radio",
    );

    const sizeRadios = this.product.querySelector(".size-radios");
    let isSlitVariantSelected = false;
    let isSizeSelected = false;

    if (slitVariationRadios.length) {
      isSlitVariantSelected = [...slitVariationRadios].some(
        (radio) => radio.checked,
      );
    } else {
      isSlitVariantSelected = true;
    }

    if (sizeRadios) {
      if (sizeRadios.querySelector(".size-radio:checked")) {
        isSizeSelected = true;
      }
    } else {
      isSizeSelected = true;
    }

    return isSizeSelected && isSlitVariantSelected;
  };
}

export default VariantSizes;
